import type { CartCountItem } from '../types/Cart.ts';

export const QUALIFIED_ADD_TO_CART_GOAL_ID = 'qualified_add_to_cart_40s';
export const QUALIFIED_ADD_TO_CART_THRESHOLD_MS = 40_000;
export const QUALIFIED_ADD_TO_CART_MAX_TICK_GAP_MS = 2_500;
export const QUALIFIED_ADD_TO_CART_VISIBLE_TAB_STALE_MS = 5_000;
export const QUALIFIED_CART_ADD_CONFIRMED_EVENT = 'wtfish:qualified-cart-add-confirmed';

export interface QualifiedAddToCartSessionCandidate {
  sessionId: string;
  startedAt: number;
}

export interface QualifiedAddToCartState extends QualifiedAddToCartSessionCandidate {
  activeTimeMs: number;
  goalDispatched: boolean;
  hasConfirmedAdd: boolean;
  lastVisibleTickAt: number | null;
  updatedAt: number;
  version: 1;
  visibleTabs: Record<string, number>;
}

interface VisibleActivityUpdate {
  isVisible: boolean;
  now: number;
  tabId: string;
}

function isFiniteNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function getItemCounts(items: CartCountItem[]) {
  return new Map(items.map((item) => [item.id, item.count]));
}

export function createQualifiedAddToCartState(session: QualifiedAddToCartSessionCandidate): QualifiedAddToCartState {
  return {
    activeTimeMs: 0,
    goalDispatched: false,
    hasConfirmedAdd: false,
    lastVisibleTickAt: null,
    sessionId: session.sessionId,
    startedAt: session.startedAt,
    updatedAt: session.startedAt,
    version: 1,
    visibleTabs: {},
  };
}

export function parseQualifiedAddToCartState(value: string | null): QualifiedAddToCartState | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as Partial<QualifiedAddToCartState>;

    if (
      parsed.version !== 1 ||
      typeof parsed.sessionId !== 'string' ||
      !parsed.sessionId ||
      !isFiniteNonNegativeNumber(parsed.startedAt) ||
      !isFiniteNonNegativeNumber(parsed.activeTimeMs) ||
      typeof parsed.goalDispatched !== 'boolean' ||
      typeof parsed.hasConfirmedAdd !== 'boolean' ||
      (parsed.lastVisibleTickAt !== null && !isFiniteNonNegativeNumber(parsed.lastVisibleTickAt)) ||
      !isFiniteNonNegativeNumber(parsed.updatedAt) ||
      !parsed.visibleTabs ||
      typeof parsed.visibleTabs !== 'object' ||
      Array.isArray(parsed.visibleTabs) ||
      !Object.values(parsed.visibleTabs).every(isFiniteNonNegativeNumber)
    ) {
      return null;
    }

    return parsed as QualifiedAddToCartState;
  } catch {
    return null;
  }
}

export function chooseQualifiedAddToCartSession(
  candidates: QualifiedAddToCartSessionCandidate[],
): QualifiedAddToCartSessionCandidate | null {
  return (
    [...candidates].sort(
      (first, second) => first.startedAt - second.startedAt || first.sessionId.localeCompare(second.sessionId),
    )[0] ?? null
  );
}

export function updateQualifiedAddToCartVisibleActivity(
  state: QualifiedAddToCartState,
  update: VisibleActivityUpdate,
  maxTickGapMs = QUALIFIED_ADD_TO_CART_MAX_TICK_GAP_MS,
  visibleTabStaleMs = QUALIFIED_ADD_TO_CART_VISIBLE_TAB_STALE_MS,
): QualifiedAddToCartState {
  const visibleTabs = Object.fromEntries(
    Object.entries(state.visibleTabs).filter(([, lastSeenAt]) => update.now - lastSeenAt <= visibleTabStaleMs),
  );
  const wasTabVisible = update.tabId in visibleTabs;
  const hadVisibleTab = Object.keys(visibleTabs).length > 0;
  const elapsedSinceLastTick = state.lastVisibleTickAt === null ? null : update.now - state.lastVisibleTickAt;
  const canAccumulate =
    (wasTabVisible || hadVisibleTab) &&
    elapsedSinceLastTick !== null &&
    elapsedSinceLastTick >= 0 &&
    elapsedSinceLastTick <= maxTickGapMs;

  let activeTimeMs = state.activeTimeMs;

  if (canAccumulate) activeTimeMs += elapsedSinceLastTick;

  if (update.isVisible) {
    visibleTabs[update.tabId] = update.now;
  } else {
    delete visibleTabs[update.tabId];
  }

  return {
    ...state,
    activeTimeMs,
    lastVisibleTickAt: Object.keys(visibleTabs).length > 0 ? update.now : null,
    updatedAt: update.now,
    visibleTabs,
  };
}

export function markQualifiedAddToCartConfirmed(state: QualifiedAddToCartState, now: number): QualifiedAddToCartState {
  if (state.hasConfirmedAdd) return state;

  return {
    ...state,
    hasConfirmedAdd: true,
    updatedAt: now,
  };
}

export function canDispatchQualifiedAddToCartGoal(state: QualifiedAddToCartState) {
  return state.hasConfirmedAdd && state.activeTimeMs >= QUALIFIED_ADD_TO_CART_THRESHOLD_MS && !state.goalDispatched;
}

export function markQualifiedAddToCartGoalDispatched(
  state: QualifiedAddToCartState,
  now: number,
): QualifiedAddToCartState {
  if (state.goalDispatched) return state;

  return {
    ...state,
    goalDispatched: true,
    updatedAt: now,
  };
}

export function dispatchQualifiedAddToCartGoal(
  state: QualifiedAddToCartState,
  now: number,
  dispatch: () => void,
): QualifiedAddToCartState {
  if (!canDispatchQualifiedAddToCartGoal(state)) return state;

  try {
    dispatch();

    return markQualifiedAddToCartGoalDispatched(state, now);
  } catch {
    return state;
  }
}

export function hasConfirmedCartIncrease(
  previousConfirmedItems: CartCountItem[],
  submittedItems: CartCountItem[],
  confirmedItems: CartCountItem[],
) {
  const previousCounts = getItemCounts(previousConfirmedItems);
  const confirmedCounts = getItemCounts(confirmedItems);

  return submittedItems.some((item) => {
    const previousCount = previousCounts.get(item.id) ?? 0;
    const confirmedCount = confirmedCounts.get(item.id) ?? 0;

    return item.count > previousCount && confirmedCount > previousCount;
  });
}

export function notifyQualifiedCartAddConfirmed() {
  if (typeof window === 'undefined') return;

  try {
    window.dispatchEvent(new Event(QUALIFIED_CART_ADD_CONFIRMED_EVENT));
  } catch {
    // Analytics notification must never affect cart synchronization.
  }
}
